import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow, PageHeader } from "../components/common";
import { GALLERY } from "../mock";
import { useSiteData } from "../context/SiteData";
import { resolveImage } from "../lib/api";

export default function Gallery() {
  const [open, setOpen] = useState(null);
  const { data, loaded } = useSiteData();

  // Real, admin-editable gallery entries when any exist; stock photo as
  // fallback for any entry with no photo uploaded yet (rather than the old
  // site's text-only placeholder — this design leans on photography).
  const GALLERY_ITEMS = loaded && data.gallery.length
    ? data.gallery.map((g, i) => ({
        image: resolveImage(g.image, GALLERY[i % GALLERY.length].image),
        tag: g.tag,
        title: g.title,
        caption: g.description,
      }))
    : GALLERY;

  useEffect(() => {
    const onKey = (e) => {
      if (open === null) return;
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o + 1) % GALLERY_ITEMS.length);
      if (e.key === "ArrowLeft") setOpen((o) => (o - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, GALLERY_ITEMS.length]);

  return (
    <>
      <PageHeader
        crumb="Gallery"
        title="A few moments from the warehouse floor"
        intro="Awards, values and genuine parts — click a photo to see the full caption."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal><Eyebrow>Documented, not staged</Eyebrow></Reveal>
        <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4 max-w-3xl">Real moments — recognition, culture, product</h2></Reveal>
        <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">No stock re-enactments — what's here is what actually happened on record. Click any photo for the full caption.</p></Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {GALLERY_ITEMS.map((g, i) => (
            <Reveal key={g.title} delay={i * 80}>
              <button
                onClick={() => setOpen(i)}
                className="group relative w-full text-left rounded-2xl overflow-hidden border border-[var(--rs-border)] card-hover"
              >
                <div className="overflow-hidden">
                  <img src={g.image} alt={g.title} className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="font-mono-label text-[11px] text-white/80 uppercase tracking-wider">{g.tag}</span>
                  <h3 className="font-head font-semibold text-white text-lg mt-1">{g.title}</h3>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      {open !== null && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setOpen(null)}><X size={30} /></button>
          <button className="absolute left-4 md:left-10 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setOpen((o) => (o - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length); }}><ChevronLeft size={40} /></button>
          <button className="absolute right-4 md:right-10 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setOpen((o) => (o + 1) % GALLERY_ITEMS.length); }}><ChevronRight size={40} /></button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={GALLERY_ITEMS[open].image} alt={GALLERY_ITEMS[open].title} className="w-full max-h-[70vh] object-contain rounded-xl" />
            <div className="mt-5 text-center">
              <span className="font-mono-label text-xs text-[#5b8cf0] uppercase tracking-wider">{GALLERY_ITEMS[open].tag}</span>
              <h3 className="font-head font-bold text-white text-xl mt-1">{GALLERY_ITEMS[open].title}</h3>
              <p className="text-white/70 text-sm mt-2 max-w-xl mx-auto">{GALLERY_ITEMS[open].caption}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
